import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, Output, EventEmitter, HostListener } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../services/toast-service';
import { BugReportService, SubmitBugReportRequest, AnalyzeBugScreenshotResponse, SubmitBugReportResponse, BugReportScreenshotItem } from '../../../services/bug-report.service';
import { ScreenshotService } from '../../../services/screenshot.service';
import { BugRecordingService } from '../../../services/bug-recording.service';
import { AppSettings } from '../../../shared/app-settings';

/** Internal model for a single image in the bug report */
interface BugImage {
  /** Raw base64 (without data: prefix) */
  originalBase64: string;
  /** Annotated base64 (null if no annotation yet) */
  annotatedBase64: string | null;
  /** data:image URL for previewing the original */
  previewUrl: string;
  /** How the image was added */
  attachmentType: 'screenshot' | 'paste' | 'upload';
  /** Display name */
  fileName: string;
}

@Component({
  selector: 'app-bug-report-button',
  templateUrl: './bug-report-button.component.html',
  styleUrls: ['./bug-report-button.component.scss']
})
export class BugReportButtonComponent implements OnInit, OnDestroy {
  @ViewChild('bugReportModal') bugReportModal!: TemplateRef<any>;
  @ViewChild('successModal') successModal!: TemplateRef<any>;

  /** Emits before screenshot capture so parent containers (e.g. notification dropdown) can close first */
  @Output() actionTriggered = new EventEmitter<void>();

  // Feature flag
  isEnabled = false;

  // State
  isCapturing = false;
  isAnalyzing = false;
  isSubmitting = false;
  showAdvanced = false;

  // Multi-image support
  readonly MAX_IMAGES = 5;
  images: BugImage[] = [];
  selectedImageIndex = 0;

  // Form fields
  title = '';
  description = '';
  severity: 'low' | 'medium' | 'high' | 'critical' = 'medium';
  currentUrl = '';
  moduleName = '';
  browserInfo = '';
  screenResolution = '';

  // Recording
  consoleErrors = '';
  networkErrors = '';

  // AI analysis result
  aiAnalysis: AnalyzeBugScreenshotResponse | null = null;

  // Submit result
  submitResult: SubmitBugReportResponse | null = null;

  /** Whether the bug report modal is open (for paste listener) */
  private modalOpen = false;

  private modalRef: NgbModalRef | null = null;
  private reportBugSub?: Subscription;

  constructor(
    private modalService: NgbModal,
    private bugReportService: BugReportService,
    private screenshotService: ScreenshotService,
    private recordingService: BugRecordingService,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    // Check if bug report feature is enabled for this domain
    try {
      const domainDetails = JSON.parse(
        localStorage.getItem(AppSettings.StorageKeys.DomainDetails) || '{}'
      );
      this.isEnabled = !!domainDetails.useBugReport;
    } catch {
      this.isEnabled = false;
    }

    // Listen for bug report requests from toast messages
    this.reportBugSub = this.toastService.reportBugRequested.subscribe(() => {
      if (this.isEnabled) {
        this.openBugReport();
      }
    });
  }

  ngOnDestroy(): void {
    this.reportBugSub?.unsubscribe();
  }

  get isRecording(): boolean {
    return this.recordingService.recording;
  }

  /** Currently selected image (or null if no images) */
  get selectedImage(): BugImage | null {
    return this.images[this.selectedImageIndex] ?? null;
  }

  /** Base64 for the annotator — always the original (annotator handles drawing) */
  get selectedImageBase64(): string | null {
    return this.selectedImage?.originalBase64 ?? null;
  }

  // ── Clipboard paste handler ──
  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (!this.modalOpen) return;
    if (this.images.length >= this.MAX_IMAGES) return;

    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          this.addImageFromFile(file, 'paste');
        }
        break; // Only first image from paste
      }
    }
  }

  /** Select a thumbnail */
  selectImage(index: number): void {
    if (index >= 0 && index < this.images.length) {
      this.selectedImageIndex = index;
    }
  }

  /** Remove an image by index */
  removeImage(index: number): void {
    if (this.images.length <= 0) return;
    this.images.splice(index, 1);
    // Adjust selected index
    if (this.selectedImageIndex >= this.images.length) {
      this.selectedImageIndex = Math.max(0, this.images.length - 1);
    }
  }

  /** Maximum image size accepted (10 MB). Bigger paste / uploads previously pumped the
   *  entire base64-data into memory + payload, easily killing the browser tab. */
  private readonly MAX_IMAGE_BYTES = 10 * 1024 * 1024;

  /** Add image from a File object (paste or file input) */
  private addImageFromFile(file: File, type: 'paste' | 'upload'): void {
    if (file.size > this.MAX_IMAGE_BYTES) {
      this.toastService.show({
        type: 'error',
        data: [this.translateService.instant('BugReport.imageTooLarge', {
          mb: (this.MAX_IMAGE_BYTES / 1024 / 1024).toFixed(0)
        })]
      });
      return;
    }
    if (file.type && !file.type.startsWith('image/')) {
      this.toastService.show({
        type: 'error',
        data: [this.translateService.instant('BugReport.invalidImageType')]
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the data:image/...;base64, prefix
      const base64 = dataUrl.split(',')[1];
      if (base64) {
        const image: BugImage = {
          originalBase64: base64,
          annotatedBase64: null,
          previewUrl: dataUrl,
          attachmentType: type,
          fileName: type === 'paste'
            ? `clipboard-${this.images.length + 1}.png`
            : file.name || `upload-${this.images.length + 1}.png`
        };
        this.images.push(image);
        // Auto-select the newly added image
        this.selectedImageIndex = this.images.length - 1;
      }
    };
    reader.readAsDataURL(file);
  }

  /** Handle file input for adding images */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const remainingSlots = this.MAX_IMAGES - this.images.length;
    const files = Array.from(input.files).slice(0, remainingSlots);

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        this.addImageFromFile(file, 'upload');
      }
    }

    // Reset input so the same file can be selected again
    input.value = '';
  }

  /**
   * Main entry point: capture screenshot then open dialog
   */
  async openBugReport(): Promise<void> {
    if (this.isCapturing) return;
    this.actionTriggered.emit();
    this.isCapturing = true;

    try {
      // Capture current page info
      this.currentUrl = window.location.href;
      this.browserInfo = navigator.userAgent;
      this.screenResolution = `${window.screen.width}x${window.screen.height}`;
      this.moduleName = this.detectCurrentModule();

      // Get any recorded errors
      if (this.recordingService.recording) {
        const errors = this.recordingService.stopRecording();
        this.consoleErrors = errors.consoleErrors.join('\n');
        this.networkErrors = errors.networkErrors.join('\n');
      }

      // Small delay to allow parent containers (notification dropdown) to close before capture
      await new Promise(r => setTimeout(r, 200));

      // Capture screenshot
      const base64 = await this.screenshotService.captureScreenshot();
      const previewUrl = this.screenshotService.toDataUrl(base64);

      // Reset form & images
      this.images = [{
        originalBase64: base64,
        annotatedBase64: null,
        previewUrl: previewUrl,
        attachmentType: 'screenshot',
        fileName: 'screenshot-1.png'
      }];
      this.selectedImageIndex = 0;
      this.title = '';
      this.description = '';
      this.severity = 'medium';
      this.aiAnalysis = null;
      this.submitResult = null;
      this.showAdvanced = false;
      this.isAnalyzing = false;
      this.isSubmitting = false;

      // Open modal
      this.modalOpen = true;
      this.modalRef = this.modalService.open(this.bugReportModal, {
        size: 'xl',
        backdrop: 'static',
        centered: true
      });
      this.modalRef.result.then(
        () => { this.modalOpen = false; },
        () => { this.modalOpen = false; }
      );
    } catch (error: any) {
      // Drop the error object — tidligere ble `error` (med base64 + bruker-beskrivelse) logget
      // til DevTools og kunne plukkes opp av browser-aggregator-extensions.
      console.error('Failed to capture screenshot:', error?.message ?? 'unknown');
      this.toastService.show({
        type: 'error',
        data: [this.translateService.instant('BugReport.screenshotFailed')]
      });
    } finally {
      this.isCapturing = false;
    }
  }

  /**
   * Toggle recording mode
   */
  toggleRecording(): void {
    if (this.recordingService.recording) {
      const errors = this.recordingService.stopRecording();
      this.consoleErrors = errors.consoleErrors.join('\n');
      this.networkErrors = errors.networkErrors.join('\n');
      this.toastService.show({
        type: 'success',
        data: [this.translateService.instant('BugReport.recordingStopped')]
      });
    } else {
      this.recordingService.startRecording();
      this.toastService.show({
        type: 'success',
        data: [this.translateService.instant('BugReport.recordingStarted')]
      });
    }
  }

  /**
   * Run AI analysis on current screenshot
   */
  analyzeWithAI(): void {
    const currentImage = this.selectedImage;
    if (!currentImage || this.isAnalyzing) return;
    this.isAnalyzing = true;

    const base64 = currentImage.annotatedBase64 || currentImage.originalBase64;
    this.bugReportService.analyzeScreenshot({
      screenshotBase64: base64,
      userDescription: this.description || undefined,
      currentUrl: this.currentUrl || undefined,
      consoleErrors: this.consoleErrors || undefined,
      networkErrors: this.networkErrors || undefined
    }).subscribe({
      next: (result) => {
        this.aiAnalysis = result;
        // Auto-fill from AI suggestions
        if (result.suggestedTitle && !this.title) {
          this.title = result.suggestedTitle;
        }
        if (result.suggestedSeverity) {
          this.severity = result.suggestedSeverity as any;
        }
        if (result.detectedModule && !this.moduleName) {
          this.moduleName = result.detectedModule;
        }
        this.isAnalyzing = false;
      },
      error: (error) => {
        console.error('AI analysis failed:', error?.message ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('BugReport.analysisFailed')]
        });
        this.isAnalyzing = false;
      }
    });
  }

  /**
   * Called when the user draws on the screenshot.
   * Stores the annotated version for the currently selected image.
   */
  onAnnotatedImage(base64: string): void {
    if (this.selectedImage) {
      this.selectedImage.annotatedBase64 = base64;
    }
  }

  /**
   * Submit the bug report
   */
  submitBugReport(): void {
    // Hard early-return on already-submitting. Without this guard a fast double-click
    // sent two identical reports and double-billed the KAI analysis.
    if (this.isSubmitting) return;

    if (!this.title.trim()) {
      this.toastService.show({
        type: 'error',
        data: [this.translateService.instant('BugReport.titleRequired')]
      });
      return;
    }

    if (!this.description.trim()) {
      this.toastService.show({
        type: 'error',
        data: [this.translateService.instant('BugReport.descriptionRequired')]
      });
      return;
    }

    this.isSubmitting = true;

    // Build screenshots array from images (use annotated version if available)
    const screenshots: BugReportScreenshotItem[] = this.images.map((img, i) => ({
      base64: img.annotatedBase64 || img.originalBase64,
      fileName: img.fileName || `image-${i + 1}.png`,
      attachmentType: img.attachmentType
    }));

    const request: SubmitBugReportRequest = {
      title: this.title.trim(),
      description: this.description.trim(),
      screenshots: screenshots.length > 0 ? screenshots : undefined,
      currentUrl: this.currentUrl || undefined,
      moduleName: this.moduleName || undefined,
      browserInfo: this.browserInfo || undefined,
      screenResolution: this.screenResolution || undefined,
      consoleErrors: this.consoleErrors || undefined,
      networkErrors: this.networkErrors || undefined,
      severity: this.severity,
      createFreshdeskTicket: true,
      aiAnalysis: this.aiAnalysis?.aiAnalysis || undefined,
      claudeCodePrompt: this.aiAnalysis?.claudeCodePrompt || undefined
    };

    this.bugReportService.submit(request).subscribe({
      next: (result) => {
        this.submitResult = result;
        this.isSubmitting = false;
        this.modalRef?.close();

        // Show success modal
        this.modalRef = this.modalService.open(this.successModal, {
          centered: true,
          backdrop: 'static'
        });
      },
      error: (error) => {
        console.error('Bug report submission failed, trying email fallback:', error?.message ?? 'unknown');
        this.submitViaEmailFallback(request);
      }
    });
  }

  /**
   * Email fallback when primary API submit fails.
   * Sends bug report via backend SMTP to hjelp@konti.no.
   * If this also fails, falls back to mailto: link.
   */
  private submitViaEmailFallback(request: SubmitBugReportRequest): void {
    this.bugReportService.submitViaEmail(request).subscribe({
      next: (result) => {
        this.isSubmitting = false;
        if (result?.emailSent) {
          this.modalRef?.close();
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('BugReport.sentViaEmail')]
          });
        } else {
          // SMTP not configured — last resort mailto
          this.openMailtoFallback(request);
        }
      },
      error: (error) => {
        console.error('Email fallback also failed, opening mailto:', error?.message ?? 'unknown');
        this.openMailtoFallback(request);
      }
    });
  }

  /**
   * Absolute last resort: open mailto: link in browser.
   */
  private openMailtoFallback(request: SubmitBugReportRequest): void {
    this.isSubmitting = false;
    this.modalRef?.close();

    const subject = encodeURIComponent(`[ePortal Feilrapport] ${request.title}`);
    const bodyParts = [
      `Tittel: ${request.title}`,
      `Beskrivelse: ${request.description}`,
      `Alvorlighetsgrad: ${request.severity}`,
      request.currentUrl ? `URL: ${request.currentUrl}` : '',
      request.moduleName ? `Modul: ${request.moduleName}` : '',
      request.browserInfo ? `Nettleser: ${request.browserInfo}` : '',
      request.screenResolution ? `Oppløsning: ${request.screenResolution}` : ''
    ].filter(Boolean).join('\n');
    const body = encodeURIComponent(bodyParts);

    window.open(`mailto:hjelp@konti.no?subject=${subject}&body=${body}`, '_self');

    this.toastService.show({
      type: 'warning',
      data: [this.translateService.instant('BugReport.mailtoFallback')]
    });
  }

  /**
   * Retake screenshot without closing modal
   */
  async retakeScreenshot(): Promise<void> {
    try {
      this.modalRef?.close();
      this.modalOpen = false;

      // Small delay to let modal close
      await new Promise(r => setTimeout(r, 300));

      const base64 = await this.screenshotService.captureScreenshot();
      const previewUrl = this.screenshotService.toDataUrl(base64);

      // Replace the first screenshot-type image, or insert at beginning
      const screenshotIdx = this.images.findIndex(img => img.attachmentType === 'screenshot');
      const newImage: BugImage = {
        originalBase64: base64,
        annotatedBase64: null,
        previewUrl: previewUrl,
        attachmentType: 'screenshot',
        fileName: 'screenshot-1.png'
      };

      if (screenshotIdx >= 0) {
        this.images[screenshotIdx] = newImage;
        this.selectedImageIndex = screenshotIdx;
      } else if (this.images.length < this.MAX_IMAGES) {
        this.images.unshift(newImage);
        this.selectedImageIndex = 0;
      }

      this.aiAnalysis = null; // Reset AI analysis for new screenshot

      // Reopen modal
      this.modalOpen = true;
      this.modalRef = this.modalService.open(this.bugReportModal, {
        size: 'xl',
        backdrop: 'static',
        centered: true
      });
      this.modalRef.result.then(
        () => { this.modalOpen = false; },
        () => { this.modalOpen = false; }
      );
    } catch (error: any) {
      console.error('Retake screenshot failed:', error?.message ?? 'unknown');
      this.toastService.show({
        type: 'error',
        data: [this.translateService.instant('BugReport.screenshotFailed')]
      });
    }
  }

  /**
   * Copy Claude Code prompt to clipboard
   */
  copyClaudePrompt(): void {
    const prompt = this.aiAnalysis?.claudeCodePrompt || this.submitResult?.claudeCodePrompt;
    if (prompt) {
      navigator.clipboard.writeText(prompt).then(() => {
        this.toastService.show({
          type: 'success',
          data: [this.translateService.instant('BugReport.promptCopied')]
        });
      });
    }
  }

  closeModal(): void {
    this.modalRef?.close();
    this.modalRef = null;
    this.modalOpen = false;
  }

  /**
   * Detect which ePortal module the user is currently in
   */
  private detectCurrentModule(): string {
    const url = window.location.pathname.toLowerCase();
    if (url.includes('/time')) return 'time';
    if (url.includes('/workorder') || url.includes('/work-order')) return 'workorder';
    if (url.includes('/crm')) return 'crm';
    if (url.includes('/admin')) return 'admin';
    if (url.includes('/system-admin')) return 'system-admin';
    if (url.includes('/reports') || url.includes('/report')) return 'reports';
    if (url.includes('/warehouse') || url.includes('/stock')) return 'warehouse';
    if (url.includes('/price')) return 'price-admin';
    if (url.includes('/fixed-asset')) return 'fixed-asset';
    if (url.includes('/integration')) return 'integration';
    return 'unknown';
  }
}
